import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axios';
import { Search, Users, Shield, CheckCircle, XCircle, Clock, Zap, Grid, List, Folder, ChevronDown, ChevronRight } from 'lucide-react';

const RbacManager = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [stats, setStats] = useState({ totalUsers: 0, totalPermissions: 0, activeRoles: 0 });
  const [groupedView, setGroupedView] = useState(true);
  const [permissionGroups, setPermissionGroups] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes, usersRes] = await Promise.all([
        axiosInstance.get('/api/rbac/roles'),
        axiosInstance.get('/api/rbac/permissions'),
        axiosInstance.get('/api/members')
      ]);
      
      setRoles(rolesRes.data);
      const permissionsArray = Array.isArray(permissionsRes.data) ? permissionsRes.data : [];
      
      if (permissionsArray.length === 0) {
        console.warn('No permissions found. Please run: node seed-permissions.js');
      }
      
      setPermissions(permissionsArray);
      const usersData = usersRes.data?.members || [];
      setUsers(usersData);
      setStats({
        totalUsers: usersData.length,
        totalPermissions: permissionsArray.length,
        activeRoles: rolesRes.data.length
      });
      
      // Create permission groups
      const groups = {
        'Core Operations': ['dashboard', 'jobcards', 'schedule', 'reminders'],
        'Customer Management': ['customers', 'vehicles', 'crm'],
        'Inventory & Procurement': ['inventory', 'purchase_orders', 'requisitions', 'stock_transfers'],
        'Financial Management': ['accounts', 'expenses', 'payments', 'invoices', 'quotations'],
        'Reports & Analytics': ['reports'],
        'User & System Management': ['members', 'vendors', 'branches', 'settings', 'rbac']
      };
      setPermissionGroups(groups);
      
      // Initialize all groups and modules as expanded
      const initialExpanded = {};
      Object.keys(groups).forEach(groupName => {
        initialExpanded[groupName] = true;
      });
      setExpandedGroups(initialExpanded);
      
      const initialModulesExpanded = {};
      permissionsArray.forEach(perm => {
        initialModulesExpanded[perm.module] = true;
      });
      setExpandedModules(initialModulesExpanded);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch RBAC data');
    } finally {
      setLoading(false);
    }
  };

  const assignRoles = async (userId, roleIds) => {
    if (submitting) return;
    
    setSubmitting(true);
    try {
      await axiosInstance.post('/api/rbac/users/assign-roles', {
        userId,
        roleIds
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      setSelectedUser(null);
      setUserRoles([]);
      setSelectedRoles([]);
    } catch (err) {
      alert(`❌ Unable to update user permissions: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchUserRoles = async (userId) => {
    try {
      const response = await axiosInstance.get(`/api/rbac/users/${userId}/permissions`);
      const permissions = response.data?.permissions || [];
      setUserRoles(permissions);
      
      // For admin users, select all available permissions by default
      const user = users.find(u => u.id === userId);
      if (user && user.role === 'ADMIN') {
        // Get all available permission slugs from the permissions state
        const allAvailablePermissions = permissions.map(p => p.slug);
        setSelectedRoles(allAvailablePermissions);
      } else {
        setSelectedRoles(permissions.map(p => p.slug));
      }
    } catch (err) {
      setUserRoles([]);
      setSelectedRoles([]);
    }
  };

  const handleUserSelect = (user) => {
    if (submitting) return;
    setSelectedUser(user);
    setUserRoles([]);
    setSelectedRoles([]);
    fetchUserRoles(user.id);
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  if (loading) return <div className="p-4">Loading RBAC data...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header with Stats */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800">Role-Based Access Control</h1>
          {showSuccess && (
            <div className="flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-lg animate-pulse">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Permissions updated successfully! 🎉</span>
            </div>
          )}
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.totalUsers}</p>
                <p className="text-sm text-gray-600">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.totalPermissions}</p>
                <p className="text-sm text-gray-600">Available Permissions</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.activeRoles}</p>
                <p className="text-sm text-gray-600">Active Roles</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      


      {/* User Role Assignment */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600" />
          User Permission Management
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-700">Select User</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="border rounded-lg overflow-y-auto bg-gray-50" style={{height: '60vh'}}>
              {users
                .filter(user => 
                  user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.email.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(user => (
                <div
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className={`p-4 border-b cursor-pointer transition-all duration-200 hover:bg-white hover:shadow-sm ${
                    selectedUser?.id === user.id ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                      user.role === 'ADMIN' ? 'bg-red-500' : user.role === 'MANAGER' ? 'bg-blue-500' : 'bg-gray-500'
                    }`}>
                      {user.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      <div className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${
                        user.role === 'ADMIN' ? 'bg-red-100 text-red-800' : 
                        user.role === 'MANAGER' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </div>
                    </div>
                    {selectedUser?.id === user.id && <CheckCircle className="w-5 h-5 text-blue-500" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {selectedUser && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                  selectedUser.role === 'ADMIN' ? 'bg-red-500' : selectedUser.role === 'MANAGER' ? 'bg-blue-500' : 'bg-gray-500'
                }`}>
                  {selectedUser.name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-800">{selectedUser.name}</h3>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                </div>
              </div>
              
              {/* Current Access Summary */}
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-800">Current Permissions ({userRoles.length})</h4>
                </div>
                {userRoles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {userRoles.slice(0, 6).map((role, index) => {
                      const displayName = typeof role === 'string' ? role : role.slug || role.permission;
                      return (
                        <span key={`${displayName}-${index}`} className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                          ✓ {displayName.replace('.', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      );
                    })}
                    {userRoles.length > 6 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                        +{userRoles.length - 6} more
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">No permissions assigned</span>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-800">Modify Permissions</h4>
                  </div>
                  <button
                    onClick={() => setGroupedView(!groupedView)}
                    className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      groupedView ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {groupedView ? <Folder className="w-4 h-4" /> : <List className="w-4 h-4" />}
                    {groupedView ? 'Grouped' : 'List View'}
                  </button>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-100 rounded border-2 border-green-500"></div>
                    <span className="text-gray-600">Currently has access</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-gray-100 rounded border-2 border-gray-300"></div>
                    <span className="text-gray-600">Available to grant</span>
                  </div>
                </div>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                assignRoles(selectedUser.id, selectedRoles);
              }}>
                <div className="space-y-4 mb-6 overflow-y-auto bg-gray-50 rounded-lg p-4" style={{height: '60vh'}}>
                  {permissions.length > 0 ? (
                    groupedView ? (
                      // Grouped View
                      Object.entries(permissionGroups).map(([groupName, moduleList]) => {
                        const groupPerms = permissions.filter(p => moduleList.includes(p.module));
                        const groupPermSlugs = groupPerms.map(p => p.slug);
                        const allGroupSelected = groupPermSlugs.every(slug => selectedRoles.includes(slug));
                        const someGroupSelected = groupPermSlugs.some(slug => selectedRoles.includes(slug));
                        
                        const handleGroupToggle = () => {
                          if (allGroupSelected) {
                            setSelectedRoles(prev => prev.filter(role => !groupPermSlugs.includes(role)));
                          } else {
                            setSelectedRoles(prev => [...new Set([...prev, ...groupPermSlugs])]);
                          }
                        };
                        
                        return (
                          <div key={groupName} className="bg-white rounded-xl p-5 shadow-md border border-gray-200">
                            <div className="flex items-center justify-between">
                              <label className="flex items-center space-x-3 cursor-pointer group hover:bg-gray-50 p-3 rounded-lg transition-colors flex-1">
                                <input 
                                  type="checkbox" 
                                  checked={allGroupSelected}
                                  ref={input => {
                                    if (input) input.indeterminate = someGroupSelected && !allGroupSelected;
                                  }}
                                  onChange={handleGroupToggle}
                                  className="w-6 h-6 rounded border-2 border-purple-300 text-purple-600 focus:ring-2 focus:ring-purple-500" 
                                />
                                <div className="flex items-center gap-3">
                                  <Folder className="w-6 h-6 text-purple-600" />
                                  <h3 className="font-bold text-purple-700 text-xl">{groupName}</h3>
                                  <span className="text-xs bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-medium">
                                    {groupPerms.length} permissions
                                  </span>
                                </div>
                              </label>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                {expandedGroups[groupName] ? 
                                  <ChevronDown className="w-5 h-5 text-gray-600" /> : 
                                  <ChevronRight className="w-5 h-5 text-gray-600" />
                                }
                              </button>
                            </div>
                            
                            {expandedGroups[groupName] && (
                              <div className="ml-12 mt-4 space-y-3">
                              {Object.entries(
                                groupPerms.reduce((acc, perm) => {
                                  if (!acc[perm.module]) acc[perm.module] = [];
                                  acc[perm.module].push(perm);
                                  return acc;
                                }, {})
                              ).map(([module, perms]) => {
                                const modulePermSlugs = perms.map(p => p.slug);
                                const allModuleSelected = modulePermSlugs.every(slug => selectedRoles.includes(slug));
                                const someModuleSelected = modulePermSlugs.some(slug => selectedRoles.includes(slug));
                                
                                const handleModuleToggle = () => {
                                  if (allModuleSelected) {
                                    setSelectedRoles(prev => prev.filter(role => !modulePermSlugs.includes(role)));
                                  } else {
                                    setSelectedRoles(prev => [...new Set([...prev, ...modulePermSlugs])]);
                                  }
                                };
                                
                                return (
                                  <div key={module} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 shadow-sm">
                                    <div className="flex items-center justify-between">
                                      <label className="flex items-center space-x-3 cursor-pointer hover:bg-white p-2 rounded-lg transition-colors flex-1">
                                        <input 
                                          type="checkbox" 
                                          checked={allModuleSelected}
                                          ref={input => {
                                            if (input) input.indeterminate = someModuleSelected && !allModuleSelected;
                                          }}
                                          onChange={handleModuleToggle}
                                          className="w-5 h-5 rounded border-2 border-blue-400 text-blue-600 focus:ring-2 focus:ring-blue-500" 
                                        />
                                        <Shield className="w-5 h-5 text-blue-600" />
                                        <span className="font-bold text-blue-800 capitalize text-lg">
                                          {module.replace('_', ' ')}
                                        </span>
                                        <span className="text-xs bg-blue-200 text-blue-900 px-3 py-1 rounded-full font-semibold">
                                          {perms.length} permissions
                                        </span>
                                      </label>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setExpandedModules(prev => ({ ...prev, [module]: !prev[module] }));
                                        }}
                                        className="p-2 hover:bg-white rounded-lg transition-colors"
                                      >
                                        {expandedModules[module] ? 
                                          <ChevronDown className="w-4 h-4 text-blue-600" /> : 
                                          <ChevronRight className="w-4 h-4 text-blue-600" />
                                        }
                                      </button>
                                    </div>
                                    {expandedModules[module] && (
                                      <div className="ml-8 mt-3 space-y-2 bg-white rounded-lg p-3">
                                        {perms.map((perm, permIndex) => (
                                          <label key={`${module}-${perm.slug}-${permIndex}`} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors group">
                                            <input 
                                              type="checkbox" 
                                              checked={selectedRoles.includes(perm.slug)} 
                                              onChange={() => handleRoleToggle(perm.slug)}
                                              className="w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500" 
                                            />
                                            <div className="flex items-center gap-2 flex-1">
                                              <span className={`text-sm font-medium ${userRoles.some(r => (typeof r === 'string' ? r : r.slug) === perm.slug) ? 'text-green-700' : 'text-gray-700'}`}>
                                                {perm.name}
                                              </span>
                                              {userRoles.some(r => (typeof r === 'string' ? r : r.slug) === perm.slug) && (
                                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                                  ✓ Active
                                                </span>
                                              )}
                                            </div>
                                          </label>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      // List View (Original)
                      Object.entries(
                        permissions.reduce((acc, perm) => {
                          if (!acc[perm.module]) acc[perm.module] = [];
                          acc[perm.module].push(perm);
                          return acc;
                        }, {})
                      ).map(([module, perms]) => {
                        const modulePermSlugs = perms.map(p => p.slug);
                        const allModuleSelected = modulePermSlugs.every(slug => selectedRoles.includes(slug));
                        const someModuleSelected = modulePermSlugs.some(slug => selectedRoles.includes(slug));
                        
                        const handleModuleToggle = () => {
                          if (allModuleSelected) {
                            setSelectedRoles(prev => prev.filter(role => !modulePermSlugs.includes(role)));
                          } else {
                            setSelectedRoles(prev => [...new Set([...prev, ...modulePermSlugs])]);
                          }
                        };
                        
                        return (
                          <div key={module} className="bg-white rounded-xl p-5 shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                            <div className="flex items-center justify-between">
                              <label className="flex items-center space-x-3 cursor-pointer group hover:bg-gray-50 p-3 rounded-lg transition-colors flex-1">
                                <input 
                                  type="checkbox" 
                                  checked={allModuleSelected}
                                  ref={input => {
                                    if (input) input.indeterminate = someModuleSelected && !allModuleSelected;
                                  }}
                                  onChange={handleModuleToggle}
                                  className="w-6 h-6 rounded border-2 border-blue-300 text-blue-600 focus:ring-2 focus:ring-blue-500" 
                                />
                                <div className="flex items-center gap-3">
                                  <Shield className="w-6 h-6 text-blue-600" />
                                  <h4 className="font-bold text-blue-700 capitalize text-xl">
                                    {module.replace('_', ' ')}
                                  </h4>
                                  <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                                    {perms.length} permissions
                                  </span>
                                </div>
                              </label>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedModules(prev => ({ ...prev, [module]: !prev[module] }));
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                {expandedModules[module] ? 
                                  <ChevronDown className="w-5 h-5 text-gray-600" /> : 
                                  <ChevronRight className="w-5 h-5 text-gray-600" />
                                }
                              </button>
                            </div>
                            {expandedModules[module] && (
                              <div className="space-y-2 ml-12 mt-4 bg-gray-50 rounded-lg p-4">
                                {perms.map((perm, permIndex) => (
                                  <label key={`${module}-${perm.slug}-${permIndex}`} className="flex items-center space-x-3 cursor-pointer hover:bg-white p-3 rounded-lg transition-colors group shadow-sm">
                                    <input 
                                      type="checkbox" 
                                      checked={selectedRoles.includes(perm.slug)} 
                                      onChange={() => handleRoleToggle(perm.slug)}
                                      className="w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500" 
                                    />
                                    <div className="flex items-center gap-2 flex-1">
                                      <span className={`text-sm font-medium ${userRoles.some(r => (typeof r === 'string' ? r : r.slug) === perm.slug) ? 'text-green-700' : 'text-gray-700'}`}>
                                        {perm.name}
                                      </span>
                                      {userRoles.some(r => (typeof r === 'string' ? r : r.slug) === perm.slug) && (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                          ✓ Active
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )
                  ) : (
                    <div className="text-gray-500 text-center py-4">Loading permissions...</div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Clock className="w-5 h-5 animate-spin" />
                      Updating Permissions...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Save Permissions 🚀
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default RbacManager;
